<script lang="ts" setup>
import type { Cluster } from '@/core/entities/property/Property';
import type {
	CircleInstanceRef,
	MapInstanceRef,
	MarkerInstanceRef
} from '@/core/service/googleMaps/types.d';

import { useMapGeometry } from '@/composables/useMapGeometry';
import { mapPromiseKey } from '@/core/service/googleMaps/utils/symbols';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import { computed, inject, onBeforeMount, ref, toRaw, watch } from 'vue';

interface ClusterRadiusProps {
	h3Cluster: Cluster;
	isSelected?: boolean;
	isClickable?: boolean;
}

const props = withDefaults(defineProps<ClusterRadiusProps>(), {
	isClickable: true
});

const emit = defineEmits<ClusterRadiusEmits>();

interface ClusterRadiusEmits {
	(e: 'click', value: { selected: boolean; cluster: Cluster }): void;
}

const { api: googleMapsApi } = useGoogleMaps();
const mapPromise = inject(mapPromiseKey);

const mapInstance: MapInstanceRef = ref();
const circleInstance: CircleInstanceRef = ref();
const clusterInstance: MarkerInstanceRef = ref();

const isSelectedModel = defineModel<boolean>('isSelected');

const rawMapInstance = computed(() => toRaw(mapInstance.value) || null);

const {
	centerCoordinates,
	isUnmapped,
	radiusInMeters,
	manipulateClusterClass,
	MAP_CONFIG
} = useMapGeometry(() => props.h3Cluster);

function setCircleInstance() {
	const maps = googleMapsApi.value?.maps;
	if (!maps || !centerCoordinates.value) {
		return;
	}

	circleInstance.value = new maps.Circle({
		center: centerCoordinates.value,
		radius: radiusInMeters.value,
		strokeColor: '#000000',
		strokeOpacity: 1,
		strokeWeight: 2,
		fillColor: '#0000001F',
		fillOpacity: 1,
		clickable: true
	});

	if (!isUnmapped.value) {
		circleInstance.value?.setMap(rawMapInstance.value);
	}
}

function setCircleInitialOptions() {
	circleInstance.value?.setOptions({
		strokeOpacity: 0,
		fillOpacity: 0,
		strokeWeight: 0
	});
}

function setCircleVisibleOptions() {
	circleInstance.value?.setOptions({
		strokeOpacity: 1,
		fillOpacity: 0.3,
		strokeWeight: 3
	});
}

function setCircleUnclickable() {
	circleInstance.value?.setOptions({
		clickable: false
	});
}

function setCircleClickable() {
	circleInstance.value?.setOptions({
		clickable: true
	});
}

function updateCircleMapInstance(newMap: google.maps.Map | null) {
	toRaw(circleInstance.value)?.setMap(newMap);
}

watch(
	() => centerCoordinates.value,
	(newCenter, oldCenter) => {
		if (newCenter && mapInstance.value) {
			// Only update center if lat/lng values actually changed
			// This prevents unnecessary re-centering when the object reference changes but values remain the same
			const hasLatChanged = newCenter.lat !== oldCenter?.lat;
			const hasLngChanged = newCenter.lng !== oldCenter?.lng;

			if (hasLatChanged || hasLngChanged) {
				mapInstance.value.setCenter(newCenter);
			}
		}
	}
);

watch(
	() => props.h3Cluster.properties.pointCount,
	count => {
		if (count) {
			updateCircleMapInstance(rawMapInstance.value);
		}

		if (!count) {
			updateCircleMapInstance(null);
		}
	}
);

watch(
	() => props.isSelected,
	isSelectedFromProps => {
		if (isSelectedFromProps) {
			manipulateClusterClass(clusterInstance.value, 'add', 'selected');
			manipulateClusterClass(clusterInstance.value, 'remove', 'hovered');
		} else {
			setCircleInitialOptions();
			manipulateClusterClass(clusterInstance.value, 'remove', 'selected');
		}
	}
);

function toggleSelection() {
	const zoom = mapInstance.value?.getZoom();
	const zoomOut = (zoom || 0) <= MAP_CONFIG.MIN_ZOOM_LEVEL;

	if (zoomOut) {
		return;
	}

	isSelectedModel.value = !isSelectedModel.value;

	if (isSelectedModel.value) {
		manipulateClusterClass(clusterInstance.value, 'add', 'selected');
		manipulateClusterClass(clusterInstance.value, 'remove', 'hovered');
	} else {
		manipulateClusterClass(clusterInstance.value, 'remove', 'selected');
		manipulateClusterClass(clusterInstance.value, 'add', 'hovered');
	}

	emit('click', { selected: isSelectedModel.value, cluster: props.h3Cluster });
}

function toggleHover(event: google.maps.MapMouseEvent) {
	if (isSelectedModel.value || isUnmapped.value) {
		return;
	}

	const zoom = mapInstance.value?.getZoom();

	if (
		(zoom || 0) <= MAP_CONFIG.MIN_ZOOM_LEVEL ||
		!event?.latLng ||
		!circleInstance.value
	) {
		return;
	}

	const center = circleInstance.value.getCenter();
	const radius = circleInstance.value.getRadius();

	if (!center || !radius) return;

	const distance = google.maps.geometry?.spherical?.computeDistanceBetween(
		event.latLng,
		center
	);

	const isInsideCircle = distance <= radius;

	if (isInsideCircle) {
		updateCircleMapInstance(rawMapInstance.value);
		manipulateClusterClass(clusterInstance.value, 'add', 'hovered');
	}

	if (!isInsideCircle) {
		manipulateClusterClass(clusterInstance.value, 'remove', 'hovered');
	}
}

function toggleZoomState() {
	const zoom: number = mapInstance.value?.getZoom() || 0;
	const zoomOut: boolean = zoom <= MAP_CONFIG.MIN_ZOOM_LEVEL;

	if (zoom) {
		if (zoomOut) {
			manipulateClusterClass(clusterInstance.value, 'remove', 'cluster-radius');
			manipulateClusterClass(
				clusterInstance.value,
				'add',
				'zoom-out-cluster-radius'
			);
			setCircleUnclickable();
		}

		if (!zoomOut) {
			manipulateClusterClass(clusterInstance.value, 'add', 'cluster-radius');
			manipulateClusterClass(
				clusterInstance.value,
				'remove',
				'zoom-out-cluster-radius'
			);

			setCircleClickable();

			if (isSelectedModel.value) {
				manipulateClusterClass(clusterInstance.value, 'add', 'selected');
			}
		}
	}
}

type MapListener = ReturnType<google.maps.Map['addListener']>;
type CircleListener = ReturnType<google.maps.Circle['addListener']>;

const mapZoomChangedListener = ref<MapListener | undefined>();
const mapIdleListener = ref<MapListener | undefined>();
const mapMouseMoveListener = ref<MapListener | undefined>();
const circleMouseMoveListener = ref<CircleListener | undefined>();
const circleClickListener = ref<CircleListener | undefined>();

onBeforeMount(async () => {
	mapInstance.value = await mapPromise;

	if (centerCoordinates.value && mapInstance.value) {
		mapInstance.value.setCenter(centerCoordinates.value);
	}

	setCircleInstance();

	mapZoomChangedListener.value = mapInstance.value?.addListener(
		'zoom_changed',
		toggleZoomState
	);

	mapIdleListener.value = mapInstance.value?.addListener('idle', async () => {
		const bounds = mapInstance.value?.getBounds();
		const centerPosition = centerCoordinates.value;

		if (centerPosition) {
			const isInsideView = bounds?.contains(centerPosition);
			const map = isInsideView ? rawMapInstance.value : null;

			if (!isUnmapped.value) {
				updateCircleMapInstance(map);
			}
		}
	});

	mapMouseMoveListener.value = mapInstance.value?.addListener(
		'mousemove',
		toggleHover
	);

	circleMouseMoveListener.value = circleInstance.value?.addListener(
		'mousemove',
		(e: google.maps.MapMouseEvent) => {
			google.maps.event.trigger(mapInstance.value!, 'mousemove', e);
		}
	);

	if (circleInstance.value) {
		google.maps.event.trigger(circleInstance.value, 'mousemove');
	}

	circleClickListener.value = circleInstance.value?.addListener('click', () => {
		if (props.isClickable) {
			toggleSelection();
		}
	});

	toggleZoomState();
});

onUnmounted(() => {
	mapZoomChangedListener.value?.remove();
	mapIdleListener.value?.remove();
	mapMouseMoveListener.value?.remove();
	circleMouseMoveListener.value?.remove();
	circleClickListener.value?.remove();
});

defineExpose({ clusterInstance, circleInstance });
</script>

<template>
	<div></div>
</template>
